import React from 'react'

import { useLocation, useNavigation } from 'react-router-dom'

import { AppContext } from '@/components/base/appcontext'
import LinkWithState from '@/components/base/link'
import { hapticSelection } from '@/lib/haptics'
import { useSubmitBusy } from '@/hooks/useSubmitBusy'

import { Button } from '@/components/ui/button'

import { Home, Search, CalendarCheck, Dumbbell, Users } from 'lucide-react'

function BottomNav() {
    const appContext = React.useContext(AppContext)
    const location = useLocation()
    const navigation = useNavigation()

    // The tab lights up on tap, not when the next screen has loaded: while
    // a navigation is pending the highlight follows where it is going, and
    // a second tap redirects it. A form submission is different: leaving
    // mid-action would abort its redirect while the action's writes still
    // land, so the tabs wait until it has reached its next screen.
    const submitting = useSubmitBusy()
    const path = (submitting ? location : (navigation.location ?? location))
        .pathname

    function isCurrent(startsWith?: string, equal?: string): boolean {
        return (
            (startsWith != null && path.startsWith(startsWith)) ||
            (equal != null && path === equal)
        )
    }

    const active = 'bg-background text-primary rounded-xl'
    const inactive = 'text-muted-foreground hover:text-primary'

    const isCommunityActive =
        isCurrent('/communityProgrammes') || isCurrent('/community')
    const isScheduleActive = isCurrent('/schedule')
    const linkCls = 'flex items-center justify-center h-full'
    const btnCls =
        'flex items-center justify-center w-full h-full transition-colors duration-150 [&_svg]:!size-5'

    const tabs = [
        {
            to: '/',
            current: isCurrent('/userProfile', '/'),
            icon: <Home className="h-5 w-5" />
        },
        {
            to: 'communityProgrammes/list',
            current: isCommunityActive,
            icon: <Search className="h-5 w-5" />
        },
        {
            to: 'coaches/list',
            current: isCurrent('/coaches') || isCurrent('/requests'),
            icon: <Users className="h-5 w-5" />
        },
        {
            to: 'schedule/list',
            current: isScheduleActive,
            icon: <CalendarCheck className="h-5 w-5" />
        },
        {
            to: 'programmes/list',
            current: isCurrent('/programmes/'),
            icon: <Dumbbell className="h-5 w-5" />
        }
    ]

    return (
        <nav
            aria-label={appContext.geti18n('base.navigation.label')}
            className="fixed bottom-2 left-4 right-4 z-50 grid grid-cols-5 items-center justify-items-stretch bg-secondary/80 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl h-12 p-1"
        >
            {tabs.map((tab) => (
                <LinkWithState key={tab.to} to={tab.to} className={linkCls}>
                    <Button
                        variant={tab.current ? 'secondary' : 'ghost'}
                        className={`${btnCls} ${tab.current ? active : inactive}`}
                        size="icon"
                        aria-current={tab.current ? 'page' : undefined}
                        disabled={submitting}
                        onClick={tab.current ? undefined : hapticSelection}
                    >
                        {tab.icon}
                    </Button>
                </LinkWithState>
            ))}
        </nav>
    )
}

export default BottomNav
